/* eslint-disable react/prop-types */
import { Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    CreateStandardApplicationPublicAccessEntrypointDocument,
    type CreateStandardApplicationPublicAccessEntrypointMutation,
    type CreateStandardApplicationPublicAccessEntrypointMutationVariables,
} from '../../../../../api/mutations/createStandardApplicationPublicAccessEntrypoint';
import {
    UpdateStandardApplicationPublicAccessEntrypointDocument,
    type UpdateStandardApplicationPublicAccessEntrypointMutation,
    type UpdateStandardApplicationPublicAccessEntrypointMutationVariables,
} from '../../../../../api/mutations/updateStandardApplicationPublicAccessEntrypoint';
import {
    type StandardApplicationPublicAccessEntrypoint,
    type StandardApplicationPublicAccessEntrypointSettings,
} from '../../../../../api/types';
import { useMultipleDealerIds } from '../../../../../components/contexts/DealerContextManager';
import InputField from '../../../../../components/fields/InputField';
import SelectField from '../../../../../components/fields/SelectField';
import { type EndpointImplementation } from './types';

const implementation: EndpointImplementation<
    StandardApplicationPublicAccessEntrypoint,
    StandardApplicationPublicAccessEntrypointSettings & { applicationModuleId?: string }
> = {
    getTitle: t => t('routerDetails:endpoints.StandardApplicationEntrypoint.title'),
    ViewPanelComponent: ({ endpoint }) => (
        <Descriptions size="small" bordered>
            <Descriptions.Item label="Module">{endpoint.applicationModule.displayName}</Descriptions.Item>
            <Descriptions.Item label="Display Name">{`Public Access - ${endpoint.displayName}`}</Descriptions.Item>
        </Descriptions>
    ),
    getIsPrivate: () => true,
    FormComponent: () => {
        const { t } = useTranslation(['routerDetails']);
        const { dealerOptions } = useMultipleDealerIds();

        return (
            <>
                <InputField
                    {...t('routerDetails:endpoints.fields.pathname', { returnObjects: true })}
                    name="pathname"
                />
                <InputField
                    {...t('routerDetails:endpoints.fields.displayName', { returnObjects: true })}
                    name="displayName"
                />
                <SelectField
                    {...t('routerDetails:endpoints.fields.dealer', { returnObjects: true })}
                    mode="multiple"
                    name="dealerIds"
                    options={dealerOptions}
                    required
                    showSearch
                />
            </>
        );
    },
    getFormInitialValues: endpoint => ({
        pathname: endpoint.pathname,
        displayName: endpoint.displayName,
        dealerIds: endpoint.dealers.map(dealer => dealer.id) || [],
    }),
    submitCreation: (apolloClient, routerId, { applicationModuleId, ...values }) => {
        if (!applicationModuleId) {
            throw new Error('applicationModuleId is required');
        }

        return apolloClient.mutate<
            CreateStandardApplicationPublicAccessEntrypointMutation,
            CreateStandardApplicationPublicAccessEntrypointMutationVariables
        >({
            mutation: CreateStandardApplicationPublicAccessEntrypointDocument,
            variables: { routerId, applicationModuleId, settings: values },
        });
    },
    submitUpdate: (apolloClient, routerId, endpointId, values) =>
        apolloClient.mutate<
            UpdateStandardApplicationPublicAccessEntrypointMutation,
            UpdateStandardApplicationPublicAccessEntrypointMutationVariables
        >({
            mutation: UpdateStandardApplicationPublicAccessEntrypointDocument,
            variables: { routerId, endpointId, settings: values },
        }),
};

export default implementation;
